import { deg2rad, rad2deg } from "../math/angles";
import { area, centroid, clipAbove, clipBand, clipXRange, type Pt } from "../math/polygon";
import type { Section } from "../geometry/section";
import type { WallInput } from "../types/input";
import type { CaseId, CaseSums, Force, ForceKind, LayerCoeff } from "../types/result";
import type { WarningCode } from "../warnings";
import { thetaFor } from "../seismic/coefficients";
import { buildSegments, integrateSegments, type DiagramSegment, type SegmentForce } from "../pressure/diagram";
import { integrateExternalLoad } from "../pressure/surcharge";
import { hydrodynamic, hydrostatic, uplift } from "../pressure/water";
import { passiveResistance } from "../pressure/passive";
import { backFaces } from "../pressure/backface";
import { rcKeyPolygon } from "../geometry/rcCantilever";
import { counterfortWeightPerMetre } from "../rc/counterfort";

export interface CaseParams { id: CaseId; kh: number; kv: number; kvFactor: number; khWater: number }
export interface AssembleContext {
  input: WallInput; section: Section; origin: Pt;
  includeFoundationEffects: boolean;            // taban: true (kaldırma, pasif, ön su); derz: false
  warn: (code: WarningCode, context?: string) => void;
}
export interface CaseLoads { forces: Force[]; sums: CaseSums; coefficients: LayerCoeff[]; Kp?: number; segments: DiagramSegment[]; segmentForces: SegmentForce[] }

export const staticCase = (): CaseParams => ({ id: "static", kh: 0, kv: 0, kvFactor: 1, khWater: 0 });

const TOL = 1e-9;

export function assembleCase(ctx: AssembleContext, cp: CaseParams): CaseLoads {
  const { input, section, origin, warn } = ctx;
  const isSeismic = cp.id !== "static";
  const kh = isSeismic ? cp.kh : 0;
  const kvF = isSeismic ? cp.kvFactor : 1;
  const gFactor = isSeismic ? input.loads.selfWeightFactorSeismic : 1;
  const inertiaOn = isSeismic && input.seismic.includeWallInertia;
  const soilInertiaOn = inertiaOn && input.seismic.includeBackfillInertia !== false;   // kama/topuk üstü dolgu + sürşarj
  const gw = input.water.gammaWater;
  const baseY = section.baseY;
  const forces: Force[] = [];

  const mk = (id: string, label: string, kind: ForceKind, V: number, H: number, x: number, y: number, note?: string): Force => {
    const armX = x - origin.x, armY = y - origin.y;
    const f: Force = { id, label, kind, V, H, x, y, armX, armY, Mres: Math.max(V, 0) * armX, Movr: Math.max(H, 0) * armY + Math.max(-V, 0) * armX };
    if (note) f.note = note;
    forces.push(f);
    return f;
  };

  // 1) Duvar ağırlığı ve ataleti
  const Ww = input.material.gamma * section.area;
  mk("W", "Duvar ağırlığı W", "weight", Ww * kvF * gFactor, 0, section.centroid.x, section.centroid.y);
  if (inertiaOn) mk("Wh", "Duvar ataleti kh·W", "inertia", 0, kh * Ww, section.centroid.x, section.centroid.y);
  // Payandalar: birim uzunluğa yayılmış net ağırlık (beton − yerini aldığı dolgu), ağırlık merkezinde
  if (input.geometry.mode === "rc-cantilever" && input.geometry.counterforts) {
    const cf = counterfortWeightPerMetre(input, input.geometry);
    if (cf && Math.abs(cf.W) > TOL) {
      mk("Wcf", "Payanda net ağırlığı Wcf (t/s yayılı)", "weight", cf.W * kvF * gFactor, 0, section.toe.x + cf.x, baseY + cf.y);
      if (inertiaOn) mk("Wcfh", "Payanda ataleti kh·Wcf", "inertia", 0, kh * cf.W, section.toe.x + cf.x, baseY + cf.y);
    }
  }
  // Betonarme kayma dişi: çokgende yok; ağırlığı taban düzeyinde eklenir, pasif direnç derinliği diş tabanına uzar (RC_KEY_APPROX)
  const keyPoly = ctx.includeFoundationEffects && input.geometry.mode === "rc-cantilever" ? rcKeyPolygon(input.geometry) : null;
  const keyDepth = keyPoly && input.geometry.mode === "rc-cantilever" ? input.geometry.key!.depth : 0;
  if (keyPoly) {
    const Wd = input.material.gamma * area(keyPoly), cd = centroid(keyPoly);
    mk("Wd", "Kayma dişi ağırlığı Wd", "weight", Wd * kvF * gFactor, 0, section.toe.x + cd.x, baseY + cd.y);
    if (inertiaOn) mk("Wdh", "Diş ataleti kh·Wd", "inertia", 0, kh * Wd, section.toe.x + cd.x, baseY + cd.y);
    warn("RC_KEY_APPROX");
  }

  // Dolgu bloğu (kama ya da kademe üstü sütun): tabaka ve su bantlarına bölünüp düşey yük olarak eklenir.
  // `minus` verilirse (kademe sütununa giren duvar parçası) bant alanlarından düşülür.
  const hw = input.water.backLevel;
  const nL = input.backfill.layers.length;
  let wedgeCount = 0;
  const addSoilBlock = (poly: Pt[], minus: Pt[] | null, yTopCut: number, tag: string): void => {
    if (poly.length < 3 || area(poly) < 1e-9) return;
    const cuts = new Set<number>([baseY, yTopCut]);
    let y = section.yTop;
    for (let j = 0; j < nL - 1; j++) { y -= input.backfill.layers[j]!.thickness; if (y > baseY + TOL && y < yTopCut - TOL) cuts.add(y); }
    if (hw !== null && hw > baseY + TOL && hw < yTopCut - TOL) cuts.add(hw);
    const ys = [...cuts].sort((a, b) => a - b);
    for (let i = 0; i + 1 < ys.length; i++) {
      const band = clipBand(poly, ys[i]!, ys[i + 1]!);
      if (band.length < 3 || area(band) < 1e-9) continue;
      let A = area(band);
      let c = centroid(band);
      const mb = minus ? clipBand(minus, ys[i]!, ys[i + 1]!) : [];
      if (mb.length >= 3 && area(mb) > 1e-9) {
        const am = area(mb), cm = centroid(mb);
        c = { x: (c.x * A - cm.x * am) / (A - am), y: (c.y * A - cm.y * am) / (A - am) };
        A -= am;
      }
      if (A < 1e-9) continue;
      const yMid = (ys[i]! + ys[i + 1]!) / 2;
      let li = nL - 1, yb = section.yTop;
      for (let j = 0; j < nL; j++) { yb -= input.backfill.layers[j]!.thickness; if (yMid > yb - TOL) { li = j; break; } }
      const L = input.backfill.layers[li]!;
      const submerged = hw !== null && yMid < hw;
      if (submerged && L.gammaSat === undefined) throw new Error("Su altındaki kama için doygun birim hacim ağırlığı gerekli (GAMMA_SAT_REQUIRED)");
      const g = submerged ? L.gammaSat! : L.gamma;
      const Wk = g * A;
      wedgeCount += 1;
      const k = wedgeCount;
      mk(`Wk${k}`, `${tag ? "Kademe üstü dolgu" : "Kama ağırlığı"} Wk${k} (${L.name ?? "Tabaka-" + (li + 1)})${tag}`, "wedge", Wk * kvF * gFactor, 0, c.x, c.y);
      if (soilInertiaOn) mk(`Wk${k}h`, `Kama ataleti kh·Wk${k}`, "inertia", 0, kh * Wk, c.x, c.y);
    }
  };
  const addSurcharge = (id: string, width: number, x: number, y: number, tag: string): void => {
    if (!(input.loads.surcharge > 0 && width > TOL)) return;
    const Q = input.loads.surcharge * width;
    mk(id, `Kama üstü sürşarj q·bk${tag}`, "surchargeOnWedge", Q * kvF, 0, x, y);
    if (soilInertiaOn) mk(`${id}h`, `Sürşarj ataleti kh·q·bk${tag}`, "inertia", 0, kh * Q, x, y);
  };

  const yC = section.planeTop.y;
  const incV = input.checks.includeActiveVerticalComponent;
  const yDyn = input.seismic.dynamicPointOfApplication === "0.6H" ? baseY + 0.6 * section.Hp : baseY + section.Hp / 2;
  const segments: DiagramSegment[] = [];
  const segmentForces: SegmentForce[] = [];
  let sumStatic = 0, sumDyn = 0;
  // İtki bileşkeleri: F düzlem/yüz normaline δ ile eğik; ang = α + δ yataydan (α: düzlemin düşeyden açısı)
  const addThrust = (segs: DiagramSegment[], forces: SegmentForce[], alpha: number, xAt: (y: number) => number, yCf: number, yDynClamp: number, tag: string): void => {
    for (const sf of forces) {
      const seg = segs.find((g) => g.index === sf.index)!;
      const ang = alpha + sf.delta;
      const id = seg.face !== undefined ? `${seg.face}.${seg.index + 1}` : `${seg.index + 1}`;
      if (sf.Fstatic > TOL) {
        const y = yCf - sf.zStatic;
        mk(`Pa${id}`, `Statik itki Pa (${seg.label})${tag}`, "earthStatic", incV ? sf.Fstatic * Math.sin(ang) : 0, sf.Fstatic * Math.cos(ang), xAt(y), y);
        sumStatic += sf.Fstatic;
      }
      if (sf.Fdyn > TOL) {
        mk(`Pad${id}`, `Dinamik itki artımı Pad (${seg.label})${tag}`, "earthDynamic", incV ? sf.Fdyn * Math.sin(ang) : 0, sf.Fdyn * Math.cos(ang), xAt(yDynClamp), yDynClamp);
        sumDyn += sf.Fdyn;
      }
    }
  };

  if (input.checks.pressureMethod === "back-face") {
    // 2–4b) Gerçek arka yüz: her kademe yüzü kendi ψᵢ ile, derinlik yüz doğrusunun dolgu yüzeyini kestiği kottan;
    //       kademe çıkıntısı üstündeki dolgu sütunu (duvar parçası düşülerek) ve üstündeki sürşarj düşey yük.
    warn("BACKFACE_METHOD");
    const tanB = Math.tan(deg2rad(input.backfill.slopeAngle));
    const { faces, ledges } = backFaces(section, tanB);
    for (const L of ledges) {
      const ySurfIn = section.surfaceYAt(L.xIn), ySurfOut = section.surfaceYAt(L.xOut);
      const col: Pt[] = [{ x: L.xIn, y: L.y }, { x: L.xOut, y: L.y }, { x: L.xOut, y: ySurfOut }, { x: L.xIn, y: ySurfIn }];
      const wallPart = clipXRange(clipAbove(section.polygon, L.y), L.xIn, L.xOut);
      const tag = ` [kademe ${L.index}]`;
      addSoilBlock(col, wallPart.length >= 3 ? wallPart : null, Math.max(ySurfIn, ySurfOut), tag);
      const xm = (L.xIn + L.xOut) / 2;
      addSurcharge(`Q${L.index}`, L.xOut - L.xIn, xm, section.surfaceYAt(xm), tag);
    }
    for (const f of faces) {
      const segsAll = buildSegments({ section, backfill: input.backfill, water: input.water, surcharge: input.loads.surcharge, kh, kvFactor: kvF, kvOnTotal: input.seismic.kvOnTotalPressure === true, warn: (c) => warn(c), plane: { psi: f.psi, yC: f.yC, yBot: f.bottom.y } });
      // Yüz üstünde kalan kısım (üst kademelerin derinlik payı) kesilir; σv birikimi korunur
      const zTopFace = f.yC - f.top.y;
      const segs = segsAll.flatMap((g): DiagramSegment[] => {
        if (g.zBot <= zTopFace + TOL) return [];
        const zTop = Math.max(g.zTop, zTopFace);
        const sigmaVTop = g.sigmaVTop + g.gammaEff * (zTop - g.zTop);
        return [{ ...g, zTop, yTop: f.yC - zTop, sigmaVTop, face: f.index, label: `${g.label} (kademe ${f.index})` }];
      }).map((g, k) => ({ ...g, index: k }));
      const forces = integrateSegments(segs, input.loads.surcharge, kvF);
      const yD = Math.min(Math.max(yDyn, f.bottom.y), f.top.y);
      addThrust(segs, forces, f.alpha, f.xAt, f.yC, yD, "");
      segments.push(...segs); segmentForces.push(...forces);
    }
  } else {
    // 2) Toprak kaması (tabaka ve su bantlarıyla)
    if (section.wedgeArea > TOL) addSoilBlock(section.wedge, null, section.planeTop.y, "");
    // 3) Kama üstü sürşarj
    const bk = section.planeTop.x - section.topBack.x;
    addSurcharge("Q", bk, section.topBack.x + bk / 2, section.yTop, "");
    // 4) Toprak itkisi (sanal düzlem)
    const segs = buildSegments({ section, backfill: input.backfill, water: input.water, surcharge: input.loads.surcharge, kh, kvFactor: kvF, kvOnTotal: input.seismic.kvOnTotalPressure === true, warn: (c) => warn(c) });
    const forces = integrateSegments(segs, input.loads.surcharge, kvF);
    addThrust(segs, forces, section.alpha, section.planeXAt, yC, yDyn, "");
    segments.push(...segs); segmentForces.push(...forces);
  }
  if (isSeismic && sumDyn > sumStatic) warn("SDS_TOO_HIGH");

  // 5) Şerit / çizgisel yükler
  for (const L of input.loads.lineLoads) {
    const r = integrateExternalLoad(L, section.Hp);
    const y = yC - r.zBar;
    mk(`Pl_${L.id}`, `${L.kind === "strip" ? "Şerit" : "Çizgisel"} yük itkisi ${L.label ?? L.id}`, "lineLoad", 0, r.F, section.planeXAt(y), y);
  }
  if (isSeismic && input.loads.lineLoads.length > 0) warn("LINELOAD_NO_SEISMIC");

  // 6) Duvar arkası su
  const hwBack = input.water.backLevel !== null ? Math.max(0, input.water.backLevel - baseY) : 0;
  if (hwBack > TOL) {
    const hs = hydrostatic(hwBack, gw);
    const y = baseY + hs.y;
    mk("Psu", "Hidrostatik su itkisi Psu", "water", 0, hs.F, section.planeXAt(y), y);
    warn("WATER_HORIZONTAL_ONLY");
    if (isSeismic && input.backfill.permeability === "permeable") {
      const hd = hydrodynamic(hwBack, gw, cp.khWater);
      const yd = baseY + hd.y;
      mk("dPsu", "Hidrodinamik su itkisi ΔPsu", "waterDynamic", 0, hd.F, section.planeXAt(yd), yd);
    }
  }

  // 7) Temel etkileri: kaldırma, pasif direnç, ön su
  let Kp: number | undefined;
  if (ctx.includeFoundationEffects) {
    // Ön taraf burun kotundan ölçülür (eğik tabanda burun topuktan B·tanα yüksek; yatay tabanda toe.y = baseY)
    const yToe = section.toe.y;
    const hwFront = input.water.frontLevel !== null ? Math.max(0, input.water.frontLevel - yToe) : 0;
    // Eğik tabanda kaldırma: düşey bileşen = ortalama basınç × yatay izdüşüm B; yatay bileşeni (dolguya doğru, lehte) ihmal edilir
    const up = uplift(section.B, hwBack, hwFront, gw);
    if (up.U > TOL) mk("U", "Kaldırma kuvveti U", "uplift", -up.U, 0, section.toe.x + up.x, yToe - section.baseSlope * up.x);
    const fs = input.foundationSoil;
    const thetaP = kh > 0 ? thetaFor(kh, kvF) : 0;
    const pr = passiveResistance({ enabled: fs.passive.enabled, Df: input.geometry.embedmentDepth + keyDepth, neglectedDepth: fs.passive.neglectedDepth, gamma: fs.gamma, phi: deg2rad(fs.phi), c: fs.c, theta: thetaP, hwFront: hwFront + keyDepth, gw });
    if (pr.Rpk > TOL) {
      Kp = pr.Kp;
      mk("Pp", "Pasif direnç Rpk", "passive", 0, -pr.Rpk * kvF, section.toe.x, yToe - keyDepth + pr.y);
    }
    if (hwFront > TOL) {
      const fw = hydrostatic(hwFront, gw);
      mk("Pwf", "Ön su basıncı", "frontWater", 0, -fw.F, section.toe.x, yToe + fw.y);
    }
  }

  // 8) Toplamlar
  const sums: CaseSums = { V: 0, U: 0, N: 0, Hdrive: 0, Hresist: 0, Rpk: 0, Mres: 0, Movr: 0 };
  for (const f of forces) {
    sums.V += Math.max(f.V, 0); sums.U += Math.max(-f.V, 0);
    sums.Hdrive += Math.max(f.H, 0); sums.Hresist += Math.max(-f.H, 0);
    if (f.kind === "passive") sums.Rpk += -f.H;
    sums.Mres += f.Mres; sums.Movr += f.Movr;
  }
  sums.N = sums.V - sums.U;

  const coefficients: LayerCoeff[] = segments.map((s) => ({
    layer: s.layerIndex, label: s.label, phi: rad2deg(s.phi), delta: rad2deg(s.delta), theta: rad2deg(s.theta),
    Kstatic: s.Kstatic, Ktotal: s.Ktotal, Kdyn: s.Ktotal - s.Kstatic, branch: s.branch,   // rapor: K_ad = K_at − K_as (Denk. 16.23 kipinde etkin artım segmentte)
    ...(s.face !== undefined ? { face: s.face } : {}),
  }));

  return { forces, sums, coefficients, Kp, segments, segmentForces };
}
